/**
 * A recipe's photo, or the stand-in when there isn't one.
 *
 * A photo is the whole reason the library reads as MEALS rather than as a spreadsheet, so
 * the no-photo case has to look deliberate rather than broken: a warm tile with the recipe's
 * initials, not a grey box with a torn-image glyph. A recipe with no photo is the normal
 * state on day one and for a long time after.
 *
 * The R2 bucket is private, so `url` is a Worker route, not a public link. It carries `?v=`
 * (the object key) so replacing a photo can never be served out of the browser cache — and
 * `onError` falls back to the initials rather than leaving a broken image behind.
 */
import { useEffect, useState } from 'react';

/**
 * Words that START WITH A LETTER win. Half of Brian's recipes are named for the clock
 * ("7:45 Close — sardines", "12:00 Break fast"), and taking the first two characters
 * blindly rendered those as "74" and "10" — a tile of digits that says nothing and reads
 * like an error code. "CS" and "BF" at least point at the name.
 */
function initials(name: string): string {
  const words = name.replace(/[^\p{L}\p{N} ]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  const lettered = words.filter((w) => /^\p{L}/u.test(w));
  const use = lettered.length ? lettered : words;
  if (!use.length) return '·';
  return (use[0][0] + (use[1]?.[0] ?? '')).toUpperCase();
}

export default function RecipePhoto({
  name,
  url,
  className = '',
}: {
  name: string;
  url: string | null;
  className?: string;
}) {
  const [broken, setBroken] = useState(false);

  // A new photo is a fresh chance to render; whatever went wrong with the last one is not
  // a fact about this one.
  useEffect(() => setBroken(false), [url]);

  if (!url || broken) {
    return (
      <div
        className={`flex items-center justify-center bg-sage-soft text-sage ${className}`}
        aria-hidden="true"
      >
        <span className="text-lg font-semibold tracking-wide">{initials(name)}</span>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={`object-cover ${className}`}
    />
  );
}
